import * as React from 'react';

import cn from 'classnames';

import { TooltipModel } from '@notino/react-styleguide';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { PortalTooltip } from '@components/PortalTooltip';
import PriceLabel from '@components/PriceLabel';
import { usePriceLogic } from '@containers/ProductDetailContainer/ProductDetail/hooks/usePriceLogic';
import { useFeatureFlags } from '@context/featureFlags/FeatureFlagsProvider';
import { typographyClass } from '@styles/common';

import { useShouldRenderEligibleAction } from '../../utils/useShouldRenderEligibleAction';
import { useHasStressOnVoucherDiscount } from '../../VoucherDiscount/useHasStressOnVoucherDiscount';

import {
  DiscountWrapper,
  OriginalPriceWrapper,
  StyledInfoIcon,
  LineThrough,
  Discount,
  PriceWrapper,
  CurrencyWrapper,
  FlexEnd,
  ActionPriceWrapper,
} from './styled';

interface IProps {
  variant: CatalogVariantFragmentFragment;
}

export const OriginalPrice = ({ variant }: IProps) => {
  const { discountText, topPrice } = usePriceLogic(variant);
  const isSmall = useHasStressOnVoucherDiscount();
  const isEligibleAction = useShouldRenderEligibleAction();
  const { experiment_pd_discount_from_rrp_hidden } = useFeatureFlags();

  if (experiment_pd_discount_from_rrp_hidden) {
    return null;
  }

  if (!topPrice && !discountText) {
    return null;
  }

  return (
    <DiscountWrapper data-testid="originalPriceDiscountWrapper">
      {topPrice && (
        <OriginalPriceWrapper
          data-testid="originalPriceWrapper"
          style={
            isEligibleAction && topPrice.actionPercentageFromRrpAbTest
              ? {
                  translate: '0 60px',
                }
              : {}
          }
        >
          <FlexEnd>
            <LineThrough
              active={topPrice.isCrossed}
              data-has-line-through={topPrice.isCrossed}
              data-testid="originalPriceLineThroughWrapper"
            >
              <PriceLabel
                price={topPrice.value}
                wrapperComponent={
                  isEligibleAction ? ActionPriceWrapper : PriceWrapper
                }
                currencyComponent={CurrencyWrapper}
              />
            </LineThrough>

            <PortalTooltip
              position={TooltipModel.Position.topRight}
              interactive={true}
              content={
                <PortalTooltip.Content>
                  {topPrice.tooltip}
                </PortalTooltip.Content>
              }
            >
              <StyledInfoIcon color="icon.disabled" height="0.9rem" />
            </PortalTooltip>
          </FlexEnd>
        </OriginalPriceWrapper>
      )}

      <Discount
        className={cn({
          [typographyClass.labelRegular]: isSmall,
          [typographyClass.labelLarge]: !isSmall,
        })}
      >
        {discountText}
      </Discount>
    </DiscountWrapper>
  );
};
